import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ZodIssue } from 'zod';

import { MobiLauncherConfig } from '@schema/index.js';
import type { MobiLauncherConfig as Config } from '@schema/index.js';
import type { Grid } from '@schema/layout.js';
import type { Profile } from '@schema/profile.js';
import type { Tile } from '@schema/tiles.js';

// (Portal-merged build) — persistence is now driven by the caller via
// `onPersist`, not localStorage. STORAGE_KEY removed.

/**
 * MobiLauncher's brand-aligned default theme. Exported so the Theme
 * editor's "Reset to defaults" button can write this same shape back
 * onto an existing tenant's profile — keeps STARTER_CONFIG (new project)
 * and reset (existing project) on the same single source of truth.
 *
 * Mirrors:
 *   - signup.ts makeStarterConfig (new-tenant first-boot)
 *   - ThemeEditor DEFAULT_LIGHT_PALETTE / DEFAULT_DARK_PALETTE (placeholders)
 *   - renderer styles/index.css :root + .profile-view[data-theme] blocks
 *     (the only source for Core tenants who don't get paletteLight/Dark
 *     application at runtime)
 */
export const STARTER_THEME: Profile['theme'] = {
  background: '#F5F7FA',
  surface:    '#FFFFFF',
  primary:    '#1976D2',
  accent:     '#03DAC6',
  text:       '#0F172A',
  textMuted:  '#64748B',
  darkMode:   'auto',
  paletteLight: {
    headerBg:   '#0F2A4A',
    headerInk:  '#FFFFFF',
    heroBg:     '#0F2A4A',
    footerBg:   '#FFFFFF',
    tileBg:     '#FFFFFF',
    tileBorder: '#E2E8F0',
    cardBg:     '#FFFFFF',
    cta:        '#1976D2',
    iconAccent: '#1976D2',
    divider:    '#E2E8F0',
  },
  paletteDark: {
    background: '#0B1220',
    surface:    '#131C2E',
    // primary + accent intentionally omitted — the renderer's brand
    // carry uses flat theme.primary / theme.accent in both modes when
    // paletteDark doesn't override, so Free tenants can edit a single
    // Primary picker and have it apply in light AND dark without
    // needing to dual-write. Pro tenants who want a contrast-adjusted
    // primary for dark mode can set paletteDark.primary explicitly.
    text:       '#E2E8F0',
    textMuted:  '#94A3B8',
    headerBg:   '#0F2A4A',
    headerInk:  '#FFFFFF',
    heroBg:     '#0F2A4A',
    footerBg:   '#131C2E',
    tileBg:     '#1A2540',
    tileBorder: '#2A3552',
    cardBg:     '#131C2E',
    cta:        '#1976D2',
    iconAccent: '#03DAC6',
    divider:    '#1F2A44',
  },
};

/**
 * Smallest valid Core config — what `New project` would seed if we had
 * a project picker. Matches the structure the runtime expects so the
 * Zod parse on every keystroke succeeds out of the gate.
 */
export const STARTER_CONFIG: Config = {
  schemaVersion: '1.0',
  tenantId: 'acme-corp',
  deploymentName: 'Untitled deployment',
  mdm: 'none',
  defaultProfileId: '11111111-1111-4111-8111-111111111111',
  profiles: [
    {
      id: '11111111-1111-4111-8111-111111111111',
      name: 'Default',
      grid: {
        columns: 4,
        rows: 3,
        tiles: [
          {
            type: 'app',
            id: '55555555-5555-4555-8555-555555555501',
            label: 'Browser',
            packageName: 'com.android.chrome',
          },
        ],
      },
      theme: STARTER_THEME,
      statusBar: { visible: true, showClock: true },
      tools: [],
      featuredTiles: [
        {
          type: 'app',
          id: 'f5555555-5555-4555-8555-555555555501',
          label: 'Feature App 1',
          packageName: 'com.example.featured1',
        },
        {
          type: 'app',
          id: 'f5555555-5555-4555-8555-555555555502',
          label: 'Feature App 2',
          packageName: 'com.example.featured2',
        },
        {
          type: 'app',
          id: 'f5555555-5555-4555-8555-555555555503',
          label: 'Feature App 3',
          packageName: 'com.example.featured3',
        },
        {
          type: 'app',
          id: 'f5555555-5555-4555-8555-555555555504',
          label: 'Feature App 4',
          packageName: 'com.example.featured4',
        },
      ],
    },
  ],
  schedules: [],
  adminAccess: {
    exitGestureEnabled: false,
    diagnosticOverlayEnabled: false,
    auditLogEnabled: false,
  },
  enabledFeatures: [],
  meta: {
    // Platform version that produced this config — keep in lockstep with
    // PLATFORM_VERSION (renderer); see documentation/internal/VERSIONING.md.
    builderVersion: '26.2.0614',
    builtAt: new Date().toISOString(),
    assetBundleHash: '0'.repeat(64),
  },
};

export interface BuilderStore {
  config: Config;
  /** Most recent Zod parse outcome — drives the validity badge. */
  parseStatus: ParseStatus;
  /**
   * Id of the profile being edited. Drives every profile-scoped mutator
   * (theme, grid, tiles, …). NOT serialized to config — this is editor
   * state, not part of the launcher payload.
   */
  activeProfileId: string;
  /** Convenience: the resolved active Profile object. */
  activeProfile: Profile;
  /** Switch which profile the editors operate on. */
  setActiveProfileId: (id: string) => void;
  /** Mutate via patch — accepts a Partial<Config> shallow-merged onto root. */
  patch: (partial: Partial<Config>) => void;
  /** Patch the active profile (identity-only mutations). */
  patchProfile: (partial: Partial<Profile>) => void;
  /** Replace the active profile's theme. */
  patchTheme: (partial: Partial<Profile['theme']>) => void;
  /** Patch the active profile's grid (columns / rows). */
  patchGrid: (partial: Partial<Pick<Grid, 'columns' | 'rows'>>) => void;
  /** Patch the active profile's status bar. */
  patchStatusBar: (partial: Partial<Profile['statusBar']>) => void;
  /** Patch the root adminAccess block. */
  patchAdminAccess: (partial: Partial<Config['adminAccess']>) => void;
  /** Patch the enabledFeatures list. */
  patchEnabledFeatures: (next: Config['enabledFeatures']) => void;
  /** Replace the root schedules array. */
  patchSchedules: (next: Config['schedules']) => void;
  /** Replace the optional device block; pass undefined to remove. */
  patchDevice: (next: Config['device']) => void;
  /** Replace the optional telemetry block; pass undefined to remove. */
  patchTelemetry: (next: Config['telemetry']) => void;
  /** Replace the optional remoteConfig block; pass undefined to remove. */
  patchRemoteConfig: (next: Config['remoteConfig']) => void;
  /** Replace the optional signage block; pass undefined to remove. */
  patchSignage: (next: Config['signage']) => void;
  /** Append a fully-formed tile to the active profile's grid. */
  addTile: (tile: Tile) => void;
  /** Drop a tile by id from the active profile's grid. */
  removeTile: (id: string) => void;
  /** Replace a tile by id (the type may change) in the active profile. */
  replaceTile: (id: string, tile: Tile) => void;
  /** Move a tile up (-1) or down (+1) in the active profile's grid. */
  moveTile: (id: string, direction: 'up' | 'down') => void;
  /**
   * Move a tile to a specific index in the active profile's grid. Used by
   * drag-and-drop: targetIndex is the *slot* where the tile should land,
   * which is the insertion point (so 0 = top, tiles.length = bottom).
   */
  moveTileTo: (id: string, targetIndex: number) => void;
  /**
   * Replace the entire tiles array of the active profile. Used by the
   * visual grid view which thinks in 2D cells; the component flattens
   * its 2D grid back to a 1D tiles array (with spacers in the middle,
   * trailing empties trimmed) and hands the result over.
   */
  setTiles: (next: Tile[]) => void;
  /** Replace the entire config (e.g., from an imported JSON file). */
  importConfig: (next: Config) => void;
  /** Reset back to STARTER_CONFIG and clear persisted state. */
  reset: () => void;
  /** Add a fresh profile, switch active to it, return its id. */
  addProfile: () => string;
  /** Copy an existing profile (new id, " (copy)" appended), switch active. */
  duplicateProfile: (id: string) => string;
  /** Remove a profile. If it was the default, reassigns to the first remaining. */
  removeProfile: (id: string) => void;
  /**
   * Move a profile to a new position in the profiles array. targetIndex
   * is the *slot* (insertion point) where the profile should land.
   * Doesn't change defaultProfileId or activeProfileId — those are id-
   * based, so reordering them is purely cosmetic.
   */
  moveProfileTo: (id: string, targetIndex: number) => void;
  /** True when there's at least one past snapshot to revert to. */
  canUndo: boolean;
  /** True when there's at least one redo snapshot ahead. */
  canRedo: boolean;
  /** Roll the config back one snapshot. No-op when canUndo is false. */
  undo: () => void;
  /** Re-apply the most recently undone snapshot. No-op when canRedo is false. */
  redo: () => void;
}

/** Cap on past+future stacks. Bounds memory when a session runs long. */
const UNDO_LIMIT = 50;

/**
 * Quiet window for snapshot coalescing — keystrokes inside this window
 * collapse into a single undo entry so typing "hello" feels like one
 * undo step, not five.
 */
const COALESCE_MS = 400;

export type ParseStatus =
  | { kind: 'valid' }
  | { kind: 'invalid'; issues: ZodIssue[] };

/**
 * Build a fresh Profile with a unique id. Used by `addProfile` and as the
 * fallback when the launcher's STARTER_CONFIG is reset.
 */
function makeProfile(name: string): Profile {
  return {
    id: crypto.randomUUID(),
    name,
    grid: { columns: 4, rows: 3, tiles: [] },
    theme: {
      background: '#0B0B0B',
      primary: '#1976D2',
      accent: '#03DAC6',
    },
    statusBar: { visible: true, showClock: true },
    tools: [],
    // Seed with four placeholder featured tiles so the hero is
    // populated on first save. Admins rename / swap them as they go.
    featuredTiles: [
      {
        type: 'app',
        id: crypto.randomUUID(),
        label: 'Feature App 1',
        packageName: 'com.example.featured1',
      },
      {
        type: 'app',
        id: crypto.randomUUID(),
        label: 'Feature App 2',
        packageName: 'com.example.featured2',
      },
      {
        type: 'app',
        id: crypto.randomUUID(),
        label: 'Feature App 3',
        packageName: 'com.example.featured3',
      },
      {
        type: 'app',
        id: crypto.randomUUID(),
        label: 'Feature App 4',
        packageName: 'com.example.featured4',
      },
    ],
  };
}

/**
 * Builder-level state. We keep it as plain `useState` rather than a reducer
 * — the surface is small and direct mutators read more clearly than a
 * dispatch-action union. When we add undo/redo / collab, this promotes
 * to useReducer or a store.
 */
export interface UseBuilderStoreOptions {
  /**
   * Seed config for the editor — typically the tenant's currently-
   * published config fetched from the licensing-server's
   * `GET /configs/:tenantId` endpoint. The hook owns the in-flight
   * draft from there; the caller is responsible for persisting via
   * `onPersist` (debounced) and for reloading on tenant switch by
   * re-mounting the hook.
   */
  initialConfig: Config;
  /**
   * Called on every config change. Use this to push to the
   * licensing-server. Errors are the caller's to handle — the hook
   * keeps the local draft regardless of save success.
   */
  onPersist?: (config: Config) => void;
}

export function useBuilderStore({
  initialConfig,
  onPersist,
}: UseBuilderStoreOptions): BuilderStore {
  const [config, setConfig] = useState<Config>(initialConfig);
  const [parseStatus, setParseStatus] = useState<ParseStatus>(() => {
    const parsed = MobiLauncherConfig.safeParse(config);
    return parsed.success ? { kind: 'valid' } : { kind: 'invalid', issues: parsed.error.issues };
  });
  const [activeProfileId, setActiveProfileIdRaw] = useState<string>(
    () => config.defaultProfileId,
  );
  /** Undo stack — pre-change snapshots, oldest first. */
  const [past, setPast] = useState<Config[]>([]);
  /** Redo stack — snapshots ahead of `config` in time. Cleared on every new edit. */
  const [future, setFuture] = useState<Config[]>([]);
  /** Timestamp of the last snapshot push; used for COALESCE_MS keystroke batching. */
  const lastSnapshotAt = useRef<number>(0);
  /** Stable ref to onPersist so the autosave effect doesn't churn on prop. */
  const onPersistRef = useRef(onPersist);
  useEffect(() => {
    onPersistRef.current = onPersist;
  }, [onPersist]);

  // Forward every config change to the caller's persistence callback.
  // Skipped on the very first render (the caller already has the
  // initialConfig and shouldn't see it echoed back as a save).
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    onPersistRef.current?.(config);
  }, [config]);

  // If active id ever dangles (deleted profile, imported config), snap to
  // the default or first profile so the editor never reads `undefined`.
  useEffect(() => {
    const stillExists = config.profiles.some((p) => p.id === activeProfileId);
    if (!stillExists) {
      const fallback = config.profiles.find((p) => p.id === config.defaultProfileId)
        ?? config.profiles[0];
      if (fallback) setActiveProfileIdRaw(fallback.id);
    }
  }, [config, activeProfileId]);

  /**
   * Apply a new config + reparse. `snapshot: false` skips pushing to the
   * undo stack — used by undo/redo themselves so they don't pollute the
   * stack with their own steps.
   */
  const update = useCallback(
    (next: Config, options?: { snapshot?: boolean }) => {
      const shouldSnapshot = options?.snapshot !== false;
      if (shouldSnapshot) {
        const now = Date.now();
        if (now - lastSnapshotAt.current > COALESCE_MS) {
          // New undo entry. Truncate from the front so the stack is bounded.
          setPast((prev) => {
            const np = [...prev, config];
            return np.length > UNDO_LIMIT ? np.slice(np.length - UNDO_LIMIT) : np;
          });
          // Any fresh edit invalidates the redo path.
          setFuture([]);
        }
        lastSnapshotAt.current = now;
      }
      setConfig(next);
      const parsed = MobiLauncherConfig.safeParse(next);
      setParseStatus(
        parsed.success ? { kind: 'valid' } : { kind: 'invalid', issues: parsed.error.issues },
      );
    },
    [config],
  );

  /** Resolved active profile, with a deterministic fallback. */
  const activeProfile = useMemo<Profile>(() => {
    return (
      config.profiles.find((p) => p.id === activeProfileId)
      ?? config.profiles.find((p) => p.id === config.defaultProfileId)
      ?? config.profiles[0]!
    );
  }, [config, activeProfileId]);

  /**
   * Helper: apply a transform to the active profile, leaving the rest
   * of the profiles array untouched.
   */
  function patchActiveProfile(transform: (p: Profile) => Profile): void {
    const idx = config.profiles.findIndex((p) => p.id === activeProfile.id);
    if (idx === -1) return;
    const nextProfiles = config.profiles.slice();
    nextProfiles[idx] = transform(activeProfile);
    update({ ...config, profiles: nextProfiles });
  }

  /** Helper: apply a transform to the active profile's grid.tiles array. */
  function patchTiles(transform: (tiles: Tile[]) => Tile[]): void {
    patchActiveProfile((p) => ({
      ...p,
      grid: { ...p.grid, tiles: transform(p.grid.tiles) },
    }));
  }

  return {
    config,
    parseStatus,
    activeProfileId: activeProfile.id,
    activeProfile,
    setActiveProfileId(id) {
      setActiveProfileIdRaw(id);
    },
    patch(partial) {
      update({ ...config, ...partial });
    },
    patchProfile(partial) {
      patchActiveProfile((p) => ({ ...p, ...partial }));
    },
    patchTheme(partial) {
      patchActiveProfile((p) => ({ ...p, theme: { ...p.theme, ...partial } }));
    },
    patchGrid(partial) {
      patchActiveProfile((p) => ({ ...p, grid: { ...p.grid, ...partial } }));
    },
    patchStatusBar(partial) {
      patchActiveProfile((p) => ({ ...p, statusBar: { ...p.statusBar, ...partial } }));
    },
    patchAdminAccess(partial) {
      update({ ...config, adminAccess: { ...config.adminAccess, ...partial } });
    },
    patchEnabledFeatures(next) {
      update({ ...config, enabledFeatures: next });
    },
    patchSchedules(next) {
      update({ ...config, schedules: next });
    },
    patchDevice(next) {
      if (next === undefined) {
        const { device: _omit, ...rest } = config;
        void _omit;
        update(rest as Config);
      } else {
        update({ ...config, device: next });
      }
    },
    patchTelemetry(next) {
      if (next === undefined) {
        const { telemetry: _omit, ...rest } = config;
        void _omit;
        update(rest as Config);
      } else {
        update({ ...config, telemetry: next });
      }
    },
    patchRemoteConfig(next) {
      if (next === undefined) {
        const { remoteConfig: _omit, ...rest } = config;
        void _omit;
        update(rest as Config);
      } else {
        update({ ...config, remoteConfig: next });
      }
    },
    patchSignage(next) {
      if (next === undefined) {
        const { signage: _omit, ...rest } = config;
        void _omit;
        update(rest as Config);
      } else {
        update({ ...config, signage: next });
      }
    },
    addTile(tile) {
      patchTiles((tiles) => [...tiles, tile]);
    },
    removeTile(id) {
      patchTiles((tiles) => tiles.filter((t) => t.id !== id));
    },
    replaceTile(id, tile) {
      patchTiles((tiles) => tiles.map((t) => (t.id === id ? tile : t)));
    },
    moveTile(id, direction) {
      patchTiles((tiles) => {
        const idx = tiles.findIndex((t) => t.id === id);
        if (idx === -1) return tiles;
        const swap = direction === 'up' ? idx - 1 : idx + 1;
        if (swap < 0 || swap >= tiles.length) return tiles;
        const next = [...tiles];
        const [a, b] = [next[idx]!, next[swap]!];
        next[idx] = b;
        next[swap] = a;
        return next;
      });
    },
    moveTileTo(id, targetIndex) {
      patchTiles((tiles) => {
        const idx = tiles.findIndex((t) => t.id === id);
        if (idx === -1) return tiles;
        if (targetIndex < 0 || targetIndex > tiles.length) return tiles;
        // Dropping above the tile's own slot, or just below it, is a no-op
        // — saves a useless undo entry on jittery drops.
        if (targetIndex === idx || targetIndex === idx + 1) return tiles;
        const next = [...tiles];
        const [item] = next.splice(idx, 1);
        // After splice, indexes after `idx` shift left by 1, so adjust.
        const insertAt = targetIndex > idx ? targetIndex - 1 : targetIndex;
        next.splice(insertAt, 0, item!);
        return next;
      });
    },
    setTiles(next) {
      patchTiles(() => next);
    },
    importConfig(next) {
      // New config → snap active to its default so we don't keep editing
      // a profile id that no longer exists.
      setActiveProfileIdRaw(next.defaultProfileId);
      update(next);
    },
    reset() {
      // Reset reseeds with STARTER_CONFIG. The Portal-merged version
      // doesn't write through to localStorage anymore — persistence
      // is the caller's responsibility via onPersist (which fires on
      // the resulting setConfig below).
      setActiveProfileIdRaw(STARTER_CONFIG.defaultProfileId);
      update(STARTER_CONFIG);
    },
    addProfile(): string {
      // Schema caps profiles at 20. The Builder enforces upstream by hiding
      // the Add button when full; this is belt-and-suspenders.
      if (config.profiles.length >= 20) return activeProfile.id;
      const fresh = makeProfile(`Profile ${config.profiles.length + 1}`);
      update({ ...config, profiles: [...config.profiles, fresh] });
      setActiveProfileIdRaw(fresh.id);
      return fresh.id;
    },
    duplicateProfile(id): string {
      const source = config.profiles.find((p) => p.id === id);
      if (!source) return activeProfile.id;
      if (config.profiles.length >= 20) return activeProfile.id;
      // Deep-copy tiles so editing one doesn't ghost into the other; grid
      // and theme are shallow-cloned in spread (they're flat enough).
      const copy: Profile = {
        ...source,
        id: crypto.randomUUID(),
        name: `${source.name} (copy)`,
        grid: {
          ...source.grid,
          tiles: source.grid.tiles.map((t) => ({ ...t, id: crypto.randomUUID() })),
        },
        theme: { ...source.theme },
        statusBar: { ...source.statusBar },
      };
      update({ ...config, profiles: [...config.profiles, copy] });
      setActiveProfileIdRaw(copy.id);
      return copy.id;
    },
    moveProfileTo(id, targetIndex) {
      const idx = config.profiles.findIndex((p) => p.id === id);
      if (idx === -1) return;
      if (targetIndex < 0 || targetIndex > config.profiles.length) return;
      // Same-slot drops are no-ops — keeps the undo stack clean.
      if (targetIndex === idx || targetIndex === idx + 1) return;
      const next = [...config.profiles];
      const [item] = next.splice(idx, 1);
      const insertAt = targetIndex > idx ? targetIndex - 1 : targetIndex;
      next.splice(insertAt, 0, item!);
      update({ ...config, profiles: next });
    },
    removeProfile(id) {
      // Can't remove the last profile — the schema requires ≥1.
      if (config.profiles.length <= 1) return;
      const nextProfiles = config.profiles.filter((p) => p.id !== id);
      // If we just removed the default, reassign to the first remaining.
      const nextDefault =
        config.defaultProfileId === id ? nextProfiles[0]!.id : config.defaultProfileId;
      // Also drop any schedules tied to the removed profile so the cross-
      // field rule (schedules[].profileId ∈ profiles) doesn't fail.
      const nextSchedules = config.schedules.filter((s) => s.profileId !== id);
      update({
        ...config,
        profiles: nextProfiles,
        defaultProfileId: nextDefault,
        schedules: nextSchedules,
      });
      if (activeProfileId === id) setActiveProfileIdRaw(nextDefault);
    },
    canUndo: past.length > 0,
    canRedo: future.length > 0,
    undo() {
      if (past.length === 0) return;
      const prev = past[past.length - 1]!;
      // Move current → future, pop past, restore prev without re-snapshotting.
      setPast(past.slice(0, -1));
      setFuture([...future, config]);
      // Force the next edit to start a fresh snapshot (and clear the redo
      // stack): 0 guarantees `now - lastSnapshotAt > COALESCE_MS`. Using
      // Date.now() here would do the opposite — the next edit would land
      // inside the coalesce window and skip both the snapshot and the
      // redo-stack clear, leaving a stale redo path after a divergent edit.
      lastSnapshotAt.current = 0;
      update(prev, { snapshot: false });
    },
    redo() {
      if (future.length === 0) return;
      const next = future[future.length - 1]!;
      setFuture(future.slice(0, -1));
      setPast([...past, config]);
      // See undo(): 0 forces the next edit to snapshot + clear redo.
      lastSnapshotAt.current = 0;
      update(next, { snapshot: false });
    },
  };
}
